import {
  Element,
  Node,
  Editor,
  Range,
  Transforms,
  TextUnit,
} from 'domains/reporter/RichTextEditor/core';
import type { CreateEnhanceEditorState } from '../../types';
import { find } from '../../utils';
import type { SectionHeaderPluginPropertyOptions } from './types';

export const enhanceEditorStateSectionHeader: CreateEnhanceEditorState<
  SectionHeaderPluginPropertyOptions
> =
  ({ pluginID }) =>
  (editor: Editor) => {
    const { isInline, deleteBackward, deleteForward } = editor;

    editor.isInline = (element: Element) => {
      return element.type === pluginID ? true : isInline(element);
    };

    editor.deleteBackward = (unit: TextUnit) => {
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        // If outside of a SectionHeader node see if the next left point (where the cursor
        // would be on delete) is a SectionHeader node. If so, remove the entire node.
        const matchEntryBefore = find(editor, (n: Node) => n.type === pluginID, {
          at: Editor.before(editor, selection),
        });

        if (matchEntryBefore != null) {
          const targetPoint = Editor.before(editor, matchEntryBefore[1]);

          Transforms.removeNodes(editor, { at: matchEntryBefore[1] });
          if (targetPoint != null) Transforms.select(editor, targetPoint);
          return;
        }
      }

      return deleteBackward(unit);
    };

    editor.deleteForward = (unit: TextUnit) => {
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        // If outside of a SectionHeader node see if the next right point (where the cursor
        // would be on delete forward) is a SectionHeader node. If so, remove the entire node.
        const matchEntryAfter = find(editor, (n: Node) => n.type === pluginID, {
          at: Editor.after(editor, selection),
        });

        if (matchEntryAfter != null) {
          const targetPoint = Editor.before(editor, matchEntryAfter[1]);

          Transforms.removeNodes(editor, { at: matchEntryAfter[1] });
          if (targetPoint != null) Transforms.select(editor, targetPoint);
          return;
        }
      }

      return deleteForward(unit);
    };

    return editor;
  };
